import type { ShortVideoProperty } from '~/core/entities/property';
import type { ComputedRef } from 'vue';

import { floatToLocaleString } from '#imports';
import { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import { getAreaLabel } from '~/core/entities/SystemEnums';
import { grammarInflection } from '~~/utils';

export function usePropertyInfo(
	property: ComputedRef<
		ShortVideoProperty | Readonly<ShortVideoProperty> | undefined
	>
) {
	const formattedPrice = computed(() => {
		const price = property.value?.askingPrice;
		if (!price) return null;
		return floatToLocaleString(price, { maximumFractionDigits: 0 });
	});

	const formattedArea = computed(() => {
		if (!property.value?.area) return null;
		return new Intl.NumberFormat('pt-BR', {
			maximumFractionDigits: 0
		}).format(Math.round(property.value.area));
	});

	const areaLabel = computed(() => {
		if (!property.value?.propertyType) return '';
		return getAreaLabel(
			property.value.propertyType.identifier as PropertyTypeEnum
		).toLowerCase();
	});

	const roomsInfo = computed(() => {
		if (!property.value) return null;
		const { suites, bedrooms } = property.value;
		if (suites && suites > 0)
			return {
				count: suites,
				label: grammarInflection(suites, 'suíte', 'suítes')
			};
		if (bedrooms && bedrooms > 0)
			return {
				count: bedrooms,
				label: grammarInflection(bedrooms, 'quarto', 'quartos')
			};
		return null;
	});

	const region = computed(() => {
		if (!property.value) return '';
		const { propertyType, region: rawRegion } = property.value;
		const trimmed = rawRegion?.trim() ?? '';
		const typeName = propertyType?.name;
		if (typeName && trimmed) return `${typeName} em ${trimmed}`;
		return trimmed || typeName || '';
	});

	const cityState = computed(() => {
		if (!property.value) return '';
		const { city, state } = property.value;
		if (city && state) return `${city} - ${state}`;
		return city ?? state ?? '';
	});

	return {
		formattedPrice,
		formattedArea,
		areaLabel,
		roomsInfo,
		region,
		cityState
	};
}
